import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, Download, Search } from "lucide-react";
import { api, exportUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, num } from "@/lib/format";
import { Card, Loading, PageHeader, Button, Badge, EmptyState } from "@/components/common";

const TYPES = [
  { key: "materials", label: "Bahan (Resep)" },
  { key: "supplies", label: "Perlengkapan" },
  { key: "products", label: "Produk Kasir" },
];

export default function StockReport() {
  const { branch } = useAuth();
  const nav = useNavigate();
  const [scope, setScope] = useState("branch");
  const [itemType, setItemType] = useState("materials");
  const [lowOnly, setLowOnly] = useState(false);
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);

  const params = useMemo(() => ({ branch_id: scope === "branch" ? branch?.id : undefined, item_type: itemType, low_only: lowOnly || undefined }), [scope, branch?.id, itemType, lowOnly]);
  useEffect(() => { setData(null); api.get("/reports/stock", params).then(setData).catch(() => setData({ rows: [], totals: { items: 0, qty: 0, value: 0, low: 0 } })); }, [params]);

  const rows = (data?.rows || []).filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()) || (r.sku || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <PageHeader title="Laporan Stok" subtitle="Preview di layar · export Excel" icon={Archive}
        actions={<Button variant="outline" onClick={() => window.open(exportUrl("stock", { branch_id: params.branch_id, item_type: itemType }), "_blank")} data-testid="stock-download"><Download size={16} /> Excel</Button>} />

      <div className="flex flex-wrap gap-2 mb-3">
        {[["branch", branch?.name || "Cabang ini"], ["all", "Semua Cabang"]].map(([k, l]) => (
          <button key={k} onClick={() => setScope(k)} data-testid={`stock-scope-${k}`} className={`px-3 h-9 rounded-full text-sm font-medium ${scope === k ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {TYPES.map((t) => <button key={t.key} onClick={() => setItemType(t.key)} data-testid={`stock-type-${t.key}`} className={`px-3 h-9 rounded-full text-sm font-medium ${itemType === t.key ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{t.label}</button>)}
      </div>
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / SKU…" data-testid="stock-search" className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-300 outline-none focus:border-primary" />
        </div>
        <button onClick={() => setLowOnly((v) => !v)} data-testid="stock-low-only" className={`px-4 h-10 rounded-xl text-sm font-medium ${lowOnly ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-600"}`}>Menipis</button>
      </div>

      {!data ? <Loading /> : (
        <>
          <Card className="p-4 mb-4 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stock-totals">
            <Info label="Jumlah item" value={`${data.totals.items} item`} />
            <Info label="Total qty fisik" value={num(data.totals.qty)} />
            <Info label="Nilai stok (HPP)" value={rupiah(data.totals.value)} />
            <Info label="Item menipis" value={`${data.totals.low} item`} />
          </Card>
          <Card className="overflow-hidden" data-testid="stock-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-400 bg-slate-50/60 border-b border-slate-100">
                  <th className="py-3 px-4 font-medium">Item</th>
                  <th className="py-3 px-4 font-medium text-right">Fisik</th>
                  <th className="py-3 px-4 font-medium text-right">Tersedia</th>
                  <th className="py-3 px-4 font-medium text-right">Min</th>
                  <th className="py-3 px-4 font-medium text-right">Nilai</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={`${r.branch_id}-${r.item_id}`} onClick={() => nav(`/stock-card?item_id=${r.item_id}${scope === "all" ? "&all=1" : ""}`)} className={`border-b border-slate-50 hover:bg-slate-50 cursor-pointer ${r.low ? "bg-amber-50/50" : ""}`} data-testid={`stock-row-${r.item_id}`}>
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-800">{r.name}</div>
                        <div className="text-xs text-slate-400">{[r.sku, r.type_label, scope === "all" ? r.branch_name : null, r.location].filter(Boolean).join(" · ")}</div>
                        {r.low && <Badge className={r.qty_available <= 0 ? "bg-rose-100 text-rose-700 mt-1" : "bg-amber-100 text-amber-700 mt-1"}>{r.qty_available <= 0 ? "HABIS" : "MENIPIS"}</Badge>}
                      </td>
                      <td className="py-3 px-4 text-right">{num(r.qty_physical)}</td>
                      <td className={`py-3 px-4 text-right font-semibold ${r.low ? "text-rose-600" : "text-slate-700"}`}>{num(r.qty_available)}</td>
                      <td className="py-3 px-4 text-right text-slate-500">{num(r.min_stock)}</td>
                      <td className="py-3 px-4 text-right">{rupiah(r.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length === 0 && <EmptyState icon={Archive} title="Tidak ada item" message="Tidak ada item untuk filter ini." />}
            </div>
          </Card>
        </>
      )}
    </>
  );
}

const Info = ({ label, value }) => (<div><div className="text-xs text-slate-400">{label}</div><div className="text-slate-800 font-semibold mt-0.5">{value}</div></div>);
